import { apiRequest } from "@/shared/lib/apiClient";
import type { CompanyAdmissionDetail, CompanyBillingInvoice, CompanyInvoiceDetail } from "./types";

export type AuditorDashboardApiResponse = {
  auditor_name: string;
  header: {
    invoices_to_audit: number;
    pending_requests?: number;
    role_badge: string;
    display_date: string;
  };
  stats: {
    total_invoices: number;
    submitted_invoices: number;
    under_review_invoices: number;
    approved_invoices: number;
    paid_invoices: number;
    rejected_invoices: number;
    pending_proformas: number;
  };
  recent_submitted: CompanyBillingInvoice[];
};

export async function fetchAuditorDashboard(): Promise<AuditorDashboardApiResponse> {
  return apiRequest<AuditorDashboardApiResponse>("/auditor/dashboard", { auth: true });
}

export type AuditorInvoicesQuery = {
  status?:
    | "pending"
    | "submitted"
    | "review"
    | "approved"
    | "rejected"
    | "audited"
    | "paid"
    | "all"
    | "pendingAudit";
  hospital_id?: number;
  company_id?: number;
};

export async function fetchAuditorInvoices(
  query: AuditorInvoicesQuery = {},
): Promise<CompanyBillingInvoice[]> {
  const params = new URLSearchParams();
  if (query.status === "all") {
    params.set("all", "1");
  } else if (query.status) {
    params.set("status", query.status === "pendingAudit" ? "pending_audit" : query.status);
  }
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  if (query.company_id) params.set("company_id", String(query.company_id));

  const qs = params.toString();
  const response = await apiRequest<{ invoices: CompanyBillingInvoice[] }>(
    `/auditor/invoices${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
  return response.invoices ?? [];
}

export async function fetchAuditorInvoice(id: number): Promise<CompanyInvoiceDetail> {
  const response = await apiRequest<{ invoice: CompanyInvoiceDetail }>(
    `/auditor/invoices/${id}`,
    { auth: true },
  );
  return response.invoice;
}

export async function startAuditorInvoiceReview(id: number): Promise<CompanyInvoiceDetail> {
  const response = await apiRequest<{ invoice: CompanyInvoiceDetail; message?: string }>(
    `/auditor/invoices/${id}/start-review`,
    { method: "POST", auth: true },
  );
  return response.invoice;
}

export async function approveAuditorInvoice(
  id: number,
  payload: { audit_notes?: string; montant_approuve?: number } = {},
): Promise<CompanyInvoiceDetail> {
  const response = await apiRequest<{ invoice: CompanyInvoiceDetail; message?: string }>(
    `/auditor/invoices/${id}/approve`,
    { method: "POST", auth: true, body: payload },
  );
  return response.invoice;
}

export async function rejectAuditorInvoice(
  id: number,
  payload: { rejection_reason: string; audit_notes?: string },
): Promise<CompanyInvoiceDetail> {
  const response = await apiRequest<{ invoice: CompanyInvoiceDetail; message?: string }>(
    `/auditor/invoices/${id}/reject`,
    { method: "POST", auth: true, body: payload },
  );
  return response.invoice;
}

export async function applyAuditorInvoiceExchangeRate(
  id: number,
  rateCdfPerUsd?: number | null,
): Promise<CompanyInvoiceDetail> {
  const body: Record<string, number> = {};
  if (rateCdfPerUsd != null && rateCdfPerUsd > 0) {
    body.rate_cdf_per_usd = rateCdfPerUsd;
  }
  const response = await apiRequest<{ invoice: CompanyInvoiceDetail; message?: string }>(
    `/auditor/invoices/${id}/exchange-rate`,
    { method: "POST", auth: true, body },
  );
  return response.invoice;
}

export type AuditorStatisticsResponse = {
  year: number;
  summary: {
    admissions: number;
    invoiced_usd: number;
    companies: number;
    hospitals: number;
  };
  indicators: {
    admissions: number;
    invoiced_usd: number;
    avg_invoice_usd: number;
    companies: number;
    hospitals: number;
    employees: number;
  };
  monthly_admissions: Array<{ month: string; value: number }>;
  monthly_invoiced: Array<{ month: string; value: number }>;
};

export async function fetchAuditorStatistics(year?: number): Promise<AuditorStatisticsResponse> {
  const params = new URLSearchParams();
  if (year) params.set("year", String(year));
  const qs = params.toString();
  return apiRequest<AuditorStatisticsResponse>(`/auditor/statistics${qs ? `?${qs}` : ""}`, {
    auth: true,
  });
}

export type AuditorAdmissionRow = {
  id: number;
  date: string;
  time: string;
  patient: string;
  patient_sub: string;
  patient_type: "employee" | "beneficiary";
  hospital: string;
  company: string;
  status: "inProgress" | "completed" | "cancelled";
  legacy_status: string;
  motif: string;
  employee_id: string;
};

export async function fetchAuditorAdmissions(query: {
  status?: string;
  date_start?: string;
  date_end?: string;
  hospital_id?: number;
  search?: string;
} = {}): Promise<{ total: number; admissions: AuditorAdmissionRow[] }> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.date_start) params.set("date_start", query.date_start);
  if (query.date_end) params.set("date_end", query.date_end);
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  if (query.search) params.set("search", query.search);
  const qs = params.toString();
  return apiRequest<{ total: number; admissions: AuditorAdmissionRow[] }>(
    `/auditor/admissions${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAuditorAdmission(id: number): Promise<CompanyAdmissionDetail> {
  const response = await apiRequest<{ admission: CompanyAdmissionDetail }>(
    `/auditor/admissions/${id}`,
    { auth: true },
  );
  return response.admission;
}

export type InvoiceAiAnalysisRow = {
  act_orig_index?: number;
  patient?: string;
  date?: string;
  act_name?: string;
  act_code?: string;
  qty?: number;
  prix_facture?: number;
  total_facture?: number;
  prix_facture_cdf?: number;
  total_facture_cdf?: number;
  prix_grille?: number;
  prix_grille_cdf?: number;
  type_obs?: string;
  observation?: string;
  proforma_valide?: boolean | null;
  is_manual?: boolean;
  is_synthesis?: boolean;
};

export type InvoiceAiActReview = {
  act_index: number;
  review_status: "pending" | "approved" | "rejected";
  montant_facture?: number;
  montant_retenu?: number;
  approve_without_proforma?: number;
  review_note?: string | null;
};

export type InvoiceAiActCorrection = {
  act_index: number;
  original_name?: string;
  corrected_name?: string;
  original_qty?: number;
  corrected_qty?: number;
  original_price?: number;
  corrected_price?: number;
  tariff_code?: string;
  note_correction?: string;
};

export type InvoiceAiAuditContext = {
  invoice_id: number;
  is_forfait_mensuel: boolean;
  can_analyze: boolean;
  rate_blocks_ai: { blocked: boolean; reason: string };
  analysis: {
    score?: number;
    summary?: string;
    analyzed_at?: string | null;
    analyzed_by?: string;
    extractor_used?: string | null;
    extractor_fallback?: boolean;
    analysis_table?: InvoiceAiAnalysisRow[];
    anomalies?: Array<{ act?: string; severity?: string; message?: string }>;
  };
  analysis_table: InvoiceAiAnalysisRow[];
  act_reviews: Record<string, InvoiceAiActReview>;
  act_corrections?: Record<string, InvoiceAiActCorrection>;
  retained_total: number;
  billed_total?: number;
  lines_total?: number;
  refracted_total?: number;
  reviews_validation: { ok: boolean; error: string; pending: number };
  job?: { status?: string; message?: string } | null;
  invoice_currency: string;
  exchange_rate_cdf: number;
  /** Validations / corrections enregistrées — effacées par une ré-analyse complète. */
  saved_work?: { reviews: number; corrections: number };
};

export async function fetchAuditorInvoiceAi(invoiceId: number): Promise<InvoiceAiAuditContext> {
  const response = await apiRequest<{ context: InvoiceAiAuditContext }>(
    `/auditor/invoices/${invoiceId}/ai`,
    { auth: true },
  );
  return response.context;
}

export async function startAuditorInvoiceAi(
  invoiceId: number,
  force = false,
): Promise<{
  success: boolean;
  async: boolean;
  status: string;
  message: string;
}> {
  return apiRequest(`/auditor/invoices/${invoiceId}/ai/start`, {
    method: "POST",
    auth: true,
    body: force ? { force: true } : undefined,
  });
}

export async function fetchAuditorInvoiceAiStatus(invoiceId: number): Promise<{
  success: boolean;
  status: string;
  message?: string;
  error?: string;
}> {
  return apiRequest(`/auditor/invoices/${invoiceId}/ai/status`, { auth: true });
}

export async function saveAuditorInvoiceAiReviews(
  invoiceId: number,
  reviews: InvoiceAiActReview[],
): Promise<{ context: InvoiceAiAuditContext; montant_retenu: number }> {
  const response = await apiRequest<{
    context: InvoiceAiAuditContext;
    montant_retenu: number;
    message?: string;
  }>(`/auditor/invoices/${invoiceId}/ai/reviews`, {
    method: "POST",
    auth: true,
    body: { reviews },
  });
  return { context: response.context, montant_retenu: response.montant_retenu };
}

export async function saveAuditorInvoiceAiCorrections(
  invoiceId: number,
  corrections: InvoiceAiActCorrection[],
): Promise<{ context: InvoiceAiAuditContext; saved: number; message: string }> {
  const response = await apiRequest<{
    context: InvoiceAiAuditContext;
    saved: number;
    message: string;
    success: boolean;
  }>(`/auditor/invoices/${invoiceId}/ai/corrections`, {
    method: "POST",
    auth: true,
    body: { corrections },
  });
  return { context: response.context, saved: response.saved, message: response.message };
}

export async function reanalyzeAuditorInvoiceAi(invoiceId: number): Promise<{
  context: InvoiceAiAuditContext;
  conformity_score: number;
  anomalies_count: number;
  corrections_applied: number;
  message: string;
}> {
  const response = await apiRequest<{
    context: InvoiceAiAuditContext;
    conformity_score: number;
    anomalies_count: number;
    corrections_applied: number;
    message: string;
    success: boolean;
  }>(`/auditor/invoices/${invoiceId}/ai/reanalyze`, {
    method: "POST",
    auth: true,
  });
  return {
    context: response.context,
    conformity_score: response.conformity_score,
    anomalies_count: response.anomalies_count,
    corrections_applied: response.corrections_applied,
    message: response.message,
  };
}

export type AuditorHospitalActsSummary = {
  id: number;
  hospital: string;
  act_count: number;
};

export type AuditorHospitalMedicalAct = {
  id: number;
  hospital_id: number;
  code: string;
  label: string;
  cost: number;
  care_type: string;
  care_category: string;
  delay_days?: number;
};

export async function fetchAuditorHospitalActsSummaries(query?: {
  has_acts?: boolean | null;
}): Promise<{
  total: number;
  hospitals: AuditorHospitalActsSummary[];
}> {
  const params = new URLSearchParams();
  if (query?.has_acts === true) params.set("has_acts", "1");
  if (query?.has_acts === false) params.set("has_acts", "0");
  const qs = params.toString();
  return apiRequest<{ total: number; hospitals: AuditorHospitalActsSummary[] }>(
    `/auditor/hospital-acts${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAuditorHospitalActs(
  hospitalId: number,
  query: { q?: string } = {},
): Promise<{ hospital_id: number; total: number; medical_acts: AuditorHospitalMedicalAct[] }> {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest(
    `/auditor/hospital-acts/${hospitalId}${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAuditorMedicalActs(query: { category?: string; q?: string } = {}) {
  const params = new URLSearchParams();
  if (query.category) params.set("category", query.category);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ total: number; medical_acts: Array<Record<string, unknown>> }>(
    `/auditor/medical-acts${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAuditorDiagnoses(query: { type?: string; q?: string } = {}) {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ total: number; diagnoses: Array<Record<string, unknown>> }>(
    `/auditor/diagnoses${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export type AuditReportListItem = {
  id: number;
  source: "audit" | "audit_reports";
  audit_date: string;
  invoice_id: number;
  invoice_number: number;
  hospital_name: string;
  company_name: string;
  auditor_name: string;
  original_amount: number;
  basket_total: number;
  status: string;
  status_label: string;
};

export type AuditReportDetail = AuditReportListItem & {
  auditor_email: string;
  diagnosis_code: string;
  report: string;
  approval_note: string;
  basket: Array<{ code: string; libelle: string; cout: number }>;
  difference: number;
  invoice_file_path: string | null;
};

export type AuditReportsQuery = {
  q?: string;
  status?: string;
};

export async function fetchAuditorAuditReports(
  query: AuditReportsQuery = {},
): Promise<{ total: number; audit_reports: AuditReportListItem[] }> {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  if (query.status) params.set("status", query.status);
  const qs = params.toString();
  return apiRequest<{ total: number; audit_reports: AuditReportListItem[] }>(
    `/auditor/audit-reports${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAuditorAuditReport(
  id: number,
  source?: "audit" | "audit_reports",
): Promise<AuditReportDetail> {
  const params = new URLSearchParams();
  if (source) params.set("source", source);
  const qs = params.toString();
  const response = await apiRequest<{ audit_report: AuditReportDetail }>(
    `/auditor/audit-reports/${id}${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
  return response.audit_report;
}

export async function submitAuditorAuditReport(payload: {
  invoice_id: number;
  report: string;
}): Promise<AuditReportDetail> {
  const response = await apiRequest<{ audit_report: AuditReportDetail; message?: string }>(
    "/auditor/audit-reports",
    { method: "POST", auth: true, body: payload },
  );
  return response.audit_report;
}

export async function exportAuditorAuditReportsCsv(query: AuditReportsQuery = {}): Promise<void> {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  if (query.status) params.set("status", query.status);
  const qs = params.toString();

  const { getApiBaseUrl } = await import("@/shared/lib/apiClient");
  const { AUTH_TOKEN_KEY } = await import("@/modules/safecheck/auth");
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const url = `${getApiBaseUrl()}/auditor/audit-reports/export${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Export impossible (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `audit-reports-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function exportAuditorInvoicesCsv(query: {
  hospital_id?: number;
  company_id?: number;
  status?: string;
  start_date?: string;
  end_date?: string;
} = {}): Promise<void> {
  const params = new URLSearchParams();
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  if (query.company_id) params.set("company_id", String(query.company_id));
  if (query.status) params.set("status", query.status);
  if (query.start_date) params.set("start_date", query.start_date);
  if (query.end_date) params.set("end_date", query.end_date);
  const qs = params.toString();

  const { getApiBaseUrl } = await import("@/shared/lib/apiClient");
  const { AUTH_TOKEN_KEY, getStoredAccountType } = await import("@/modules/safecheck/auth");
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const prefix = getStoredAccountType() === "admin" ? "/admin" : "/auditor";
  const url = `${getApiBaseUrl()}${prefix}/exports/invoices${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Export impossible (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function exportAuditorAuditsCsv(query: AuditReportsQuery = {}): Promise<void> {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  if (query.status) params.set("status", query.status);
  const qs = params.toString();

  const { getApiBaseUrl } = await import("@/shared/lib/apiClient");
  const { AUTH_TOKEN_KEY, getStoredAccountType } = await import("@/modules/safecheck/auth");
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const prefix = getStoredAccountType() === "admin" ? "/admin" : "/auditor";
  const url = `${getApiBaseUrl()}${prefix}/exports/audits${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Export impossible (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `audits-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
